import React, { useState } from 'react';
import { getBooking, cancelBooking } from '../services/api';

export default function ManageBooking({ onError }) {
  const [confirmationId, setConfirmationId] = useState('');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!confirmationId.trim()) return;
    onError(null);
    setLoading(true);
    setBooking(null);

    try {
      const result = await getBooking(confirmationId.trim().toUpperCase());
      setBooking(result);
    } catch (err) {
      const msg =
        err.response?.data?.details?.message ||
        err.response?.data?.error ||
        err.message ||
        'Booking lookup failed';
      onError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(`Are you sure you want to cancel booking ${confirmationId}?`)) return;
    onError(null);
    setCancelling(true);

    try {
      await cancelBooking({ confirmationId: confirmationId.trim().toUpperCase() });
      alert(`Booking ${confirmationId} has been cancelled.`);
      setBooking(null);
      setConfirmationId('');
    } catch (err) {
      const msg =
        err.response?.data?.details?.message ||
        err.response?.data?.error ||
        err.message ||
        'Cancellation failed';
      onError(msg);
    } finally {
      setCancelling(false);
    }
  };

  // Extract display info from Sabre getBooking response
  const bookingData = booking?.GetBookingResponse || booking;
  const travelers = bookingData?.travelers || [];
  const flights = bookingData?.flights || bookingData?.air?.flights || [];

  return (
    <div className="manage-card">
      <h2>Manage Booking</h2>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
        Look up an existing reservation using the Sabre <strong>GET Booking</strong> REST API, or
        cancel it using the <strong>Cancel Booking</strong> REST API.
      </p>

      <form onSubmit={handleLookup}>
        <div className="lookup-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Confirmation Code (PNR)</label>
            <input
              type="text"
              placeholder="e.g. ABCDEF"
              maxLength={6}
              value={confirmationId}
              onChange={(e) => setConfirmationId(e.target.value.toUpperCase())}
              required
              style={{ textTransform: 'uppercase', fontFamily: 'monospace', fontSize: 16 }}
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? 'Looking up...' : 'Retrieve Booking'}
          </button>
        </div>
      </form>

      {booking && (
        <div className="booking-detail-section">
          <h3>Booking Details</h3>

          {/* Travelers */}
          {travelers.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                Travelers
              </div>
              {travelers.map((t, i) => (
                <div key={i} style={{ fontSize: 14, padding: '4px 0' }}>
                  {t.givenName || t.firstName} {t.surname || t.lastName} ({t.passengerTypeCode || 'ADT'})
                </div>
              ))}
            </div>
          )}

          {/* Flights */}
          {flights.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                Flights
              </div>
              {flights.map((f, i) => (
                <div key={i} style={{ fontSize: 14, padding: '4px 0' }}>
                  {f.airlineCode || f.carrier?.marketing}
                  {f.flightNumber || f.carrier?.marketingFlightNumber}:{' '}
                  {f.fromAirportCode || f.departure?.airport} &rarr;{' '}
                  {f.toAirportCode || f.arrival?.airport}
                </div>
              ))}
            </div>
          )}

          {/* Raw response */}
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: '#6b7280' }}>
              View raw API response
            </summary>
            <pre
              style={{
                background: '#f3f4f6',
                padding: 16,
                borderRadius: 8,
                fontSize: 12,
                overflow: 'auto',
                maxHeight: 400,
                marginTop: 8,
              }}
            >
              {JSON.stringify(booking, null, 2)}
            </pre>
          </details>

          {/* Actions */}
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <button className="btn btn-danger btn-sm" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling...' : 'Cancel Booking'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
